import { ServerClock } from './clock';
import { RemotePlayback } from './interpolation';
import { Predictor, type Direction } from './prediction';
import { PROTOCOL_VERSION, parseServer, type ClientMessage, type Entity, type ServerMessage, type Interaction } from './protocol';
import { ClientState } from './state';
import {remoteWorld,worldRoom,worldSocketURL,worldSocketTicket} from './world-transport';

export class Connection {
  readonly state = new ClientState();
  readonly clock = new ServerClock();
  readonly remote = new RemotePlayback();
  status = 'Disconnected';
  private socket: WebSocket | null = null;
  private connectGeneration=0;
  private seq = 0;
  private pingId = 0;
  private pending = new Map<number, number>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private predictor: Predictor | null = null;
  private frameTime: number | null = null;
  private accumulator = 0;
  private previewDirection: Direction | null = null;
  private lastState = 0;
  private suspended = false;
  private recovering = false;
  private receivedBytes = 0;
  private sentBytes = 0;
  private started = 0;
  private recoveries = 0;
  private barrierTime = 0;
  private stallGraceUntil = 0;
  private pauseGraceState = -1;
  private clockWarming = false;
  private latestServerMS = 0;
  roomID = 'lobby';
  chat: Extract<ServerMessage, { type: 'chat' }>[] = [];
  socialError = '';
  interactions: Interaction[] = [];
  interactionError = '';
  selectedInteraction: { id: string; name: string; kind: 'seat' | 'pair' | 'gesture' } | null = null;
  interactionStarting = 0;
  private interactionSeq = 0;
  private readiness = new Map<string, number>();
  edits = new Map<string, Extract<ServerMessage, { type: 'edit_state' }>>();
  private editRequests = new Map<string, { resolve: (lease: string) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  constructor(private changed: () => void, private revalidate: () => void = () => {}, private environmentChanged: () => void = () => {}) {}

  connect(roomID = this.roomID): void {
    this.roomID = roomID;
    worldRoom(roomID);
    this.disconnect(); this.status = 'Connecting'; this.started = performance.now(); this.changed();
    const generation=++this.connectGeneration;
    if(remoteWorld()){void worldSocketTicket().then(ticket=>{if(generation===this.connectGeneration)this.openSocket(ticket);}).catch(()=>{if(generation===this.connectGeneration){this.status='World authentication unavailable';this.changed();this.revalidate();}});}else this.openSocket();
  }
  private openSocket(ticket?:string):void{
    const ws = new WebSocket(worldSocketURL());
    this.socket = ws;
    ws.addEventListener('open', () => { if (this.socket === ws) {if(ticket)ws.send(JSON.stringify({version:1,type:'socket.authenticate',ticket}));this.send({ type: 'hello', protocol: PROTOCOL_VERSION });} });
    ws.addEventListener('message', event => {
      if (this.socket !== ws) return;
      try {
        if (typeof event.data !== 'string') throw new Error('Expected text protocol');
        this.receivedBytes += new TextEncoder().encode(event.data).length;
        const message = parseServer(event.data), now = performance.now();
        if (message.type === 'welcome') {
          this.remote.intervalMS = 1000 / message.replicationHz;
          this.send({ type: 'join', requestId: crypto.randomUUID(), roomId: this.roomID });
        } else if (message.type === 'error') {
          if (['muted', 'chat_forbidden', 'chat_rate_limited'].includes(message.code)) this.socialError = message.message;
          else if (message.code !== 'stale_epoch') this.status = `${message.code}: ${message.message}`;
        } else if (message.type === 'interactions') {
          if (message.epoch === this.state.epoch) {
            const previous = this.ownInteraction;
            this.interactions = message.items;
            if (this.ownInteraction) this.interactionStarting = 0;
            for (const id of this.readiness.keys()) if (!message.items.some(i => i.id === id)) this.readiness.delete(id);
            const anchored = (i: Interaction | undefined) => i && i.phase !== 'invited' && i.kind !== 'gesture';
            if (anchored(previous) || anchored(this.ownInteraction)) { this.accumulator = 0; this.previewDirection = null; }
          }
        } else if (message.type === 'interaction_result') {
          if (message.epoch === this.state.epoch && message.seq === this.interactionStarting) this.interactionStarting = 0;
          if (message.epoch === this.state.epoch && !['ok', 'stale_readiness', 'stale_epoch'].includes(message.code)) this.interactionError = message.code.replaceAll('_', ' ');
        } else if (message.type === 'interaction_ended') {
          if (message.epoch === this.state.epoch && this.ownInteraction?.id === message.id) this.interactionError = `Interaction ${message.reason.replaceAll('_', ' ')}`;
        } else if (message.type === 'edit_result') {
          if (message.epoch === this.state.epoch) {
            const pending = this.editRequests.get(message.requestId);
            if (pending) { this.editRequests.delete(message.requestId); clearTimeout(pending.timer); if (message.code === 'ok' || message.code === 'released') pending.resolve(message.leaseId); else pending.reject(new Error(message.code.replaceAll('_', ' '))); }
          }
        } else if (message.type === 'edit_state') {
          if (message.epoch === this.state.epoch) { if (message.holderId) this.edits.set(message.objectId, message); else this.edits.delete(message.objectId); }
        } else if (message.type === 'chat') {
          if (message.epoch === this.state.epoch && !this.chat.some(m => m.id === message.id)) { this.chat.push(message); if (this.chat.length > 100) this.chat.shift(); }
        } else if (message.type === 'pong') {
          const sent = this.pending.get(message.id);
          if (sent !== undefined && sent === message.clientMs && message.epoch === this.state.epoch) {
            this.pending.delete(message.id);
            this.clock.add(sent, now, message.serverMs);
            if (this.clockWarming && (this.clock.estimate?.samples ?? 0) >= 5) {
              if (this.timer !== null) clearInterval(this.timer);
              this.clockWarming = false; this.timer = setInterval(() => this.ping(), 2000);
            }
          }
        } else {
          const previousEpoch = this.state.epoch;
          if (this.state.apply(message)) {
          if (message.type === 'snapshot') {
            if (this.timer !== null) clearInterval(this.timer);
            this.timer = null; this.pending.clear(); this.clock.reset(); this.remote.reset();
            this.seq = 0; this.accumulator = 0; this.previewDirection = null; this.recovering = false;
            this.interactions = []; this.interactionError = ''; this.interactionStarting = 0; this.interactionSeq = 0; this.readiness.clear(); this.edits.clear();
            for (const pending of this.editRequests.values()) { clearTimeout(pending.timer); pending.reject(new Error('Room environment changed')); } this.editRequests.clear();
            this.latestServerMS = message.serverMs;
            this.predictor = new Predictor(message.map); this.predictor.reconcile(this.state.entities.get(this.state.selfId)!);
            this.lastState = now; this.frameTime = now;
            this.remote.observe(this.state.entities, this.state.selfId, message.serverMs, now);
            this.status = `Connected · ${this.roomID}`; this.clockWarming = true; this.ping();
            this.timer = setInterval(() => this.ping(), 200);
            if (previousEpoch) this.environmentChanged();
          } else if (message.type === 'state') {
            this.latestServerMS = message.serverMs;
            if (now - this.lastState > 1000 && !this.recovering) this.recover();
            this.lastState = now;
            this.predictor?.reconcile(this.state.entities.get(this.state.selfId)!);
            this.remote.observe(this.state.entities, this.state.selfId, message.serverMs, now);
            if (this.recovering && !this.predictor?.waiting) { this.recovering = false; this.status = `Connected · ${this.roomID}`; }
          } else if (message.type === 'contents') {
            if (this.state.map) this.predictor?.updateMap(this.state.map);
          } else if (message.type === 'spawn' || message.type === 'enter') {
            // A new visibility interval must not reuse any prior trajectory.
            this.remote.remove(message.entity.id);
            this.remote.observe(this.state.entities, this.state.selfId, message.serverMs, now);
          } else if (message.type === 'leave' || message.type === 'despawn') this.remote.remove(message.id);
          }
        }
        this.changed();
      } catch {
        this.disconnect(); this.status = 'Protocol error — reconnect to retry'; this.changed();
      }
    });
    ws.addEventListener('close', () => {
      if (this.socket !== ws) return;
      const prior = this.status;
      this.disconnect(); this.status = prior.includes(':') ? prior : 'Disconnected — reconnect to join'; this.changed();
      this.revalidate();
    });
    ws.addEventListener('error', () => { if (this.socket === ws) { this.status = 'Connection unavailable'; this.changed(); } });
  }
  private ping(): void {
    if (this.pending.size >= 8) { this.disconnect(); this.status = 'Clock heartbeat timed out'; this.changed(); return; }
    const id = ++this.pingId, clientMs = performance.now(); this.pending.set(id, clientMs);
    this.send({ type: 'ping', id, clientMs });
  }
  sendChat(text: string): void {
    text = text.trim(); if (!this.state.epoch || !text || [...text].length > 400) return;
    this.socialError = ''; this.send({ type: 'chat', epoch: this.state.epoch, text });
  }
  interact(action: Extract<ClientMessage, { type: 'interaction' }>['action'], targetId: string, generation = 0, assetId = ''): void {
    if (!this.state.epoch || this.interactionSeq === 2147483647) return;
    if (action !== 'ready' && action !== 'failed') this.interactionError = '';
    const seq = ++this.interactionSeq;
    if (['sit', 'invite', 'gesture'].includes(action)) this.interactionStarting = seq;
    if (action === 'cancel') this.interactionStarting = 0;
    this.send({ type: 'interaction', epoch: this.state.epoch, seq, action, targetId, generation, assetId });
  }
  reportReadiness(check: (interaction: Interaction) => 'loading' | 'ready' | 'failed'): void {
    for (const i of this.interactions) {
      if (i.phase !== 'preparing' || this.readiness.get(i.id) === i.generation) continue;
      const state = check(i); if (state === 'loading') continue;
      this.readiness.set(i.id, i.generation); this.interact(state, i.id, i.generation);
    }
  }
  get ownInteraction(): Interaction | undefined { return this.interactions.find(i => i.roles.some(r => r.entityId === this.state.selfId)); }
  get resynchronizing(): boolean { return this.recovering; }
  editLease(action: 'acquire' | 'renew' | 'release', objectId: string, revision: number, leaseId = ''): Promise<string> {
    if (!this.state.epoch || this.editRequests.size >= 4) return Promise.reject(new Error('Editing unavailable'));
    const requestId = crypto.randomUUID();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this.editRequests.delete(requestId); reject(new Error('Edit request timed out')); }, 4000);
      this.editRequests.set(requestId, { resolve, reject, timer });
      this.send({ type: 'edit_lease', epoch: this.state.epoch, requestId, objectId, revision, action, leaseId });
    });
  }
  preview(objectId: string, leaseId: string, value: Extract<ClientMessage, { type: 'edit_preview' }>['value']): void {
    if (this.state.epoch) this.send({ type: 'edit_preview', epoch: this.state.epoch, objectId, leaseId, value });
  }
  input(x: number, z: number, yaw?: number): void {
    if (!this.state.epoch || !this.predictor || this.predictor.waiting || this.suspended) return;
    if (this.seq === 2147483647) { this.connect(); return; }
    const seq = this.seq + 1;
    if (!this.predictor.input(seq, { x, z, yaw }, performance.now())) { this.recover(); return; }
    this.seq = seq; this.send({ type: 'input', epoch: this.state.epoch, seq, x, z, ...(yaw === undefined ? {} : { yaw }) });
  }
  stop(): void {
    this.accumulator = 0; this.previewDirection = null;
    if (!this.state.epoch || !this.predictor || this.predictor.waiting) return;
    if (this.seq === 2147483647) { this.connect(); return; }
    this.predictor.stop(++this.seq);
    this.barrierTime = performance.now();
    this.send({ type: 'stop', epoch: this.state.epoch, seq: this.seq });
  }
  suspend(): void { this.stop(); this.suspended = true; this.frameTime = null; }
  resume(): void {
    this.suspended = false; this.frameTime = null;
    if (!this.state.epoch) { this.connect(); return; }
    this.recover();
  }
  private recover(): void {
    if (this.recovering || !this.predictor) return;
    this.stop(); this.remote.reset(); this.accumulator = 0; this.recovering = true; this.recoveries++;
    this.status = 'Resynchronizing movement';
  }

  /** Fixed simulation/input cadence; fractional render prediction runs every frame. */
  frame(now: number, direction: Direction): Map<string, Entity> {
    if (!this.state.map || !this.predictor) return new Map();
    const elapsed = this.frameTime === null ? 0 : Math.max(0, now - this.frameTime); this.frameTime = now;
    // Shader compilation / large asset setup can pause the main thread while
    // WebSocket replies are already buffered. Freeze prediction as usual, but
    // give the event loop a short opportunity to consume them before timing out.
    if (elapsed > 200 && now - this.lastState > 250 && this.pauseGraceState !== this.lastState) { this.pauseGraceState = this.lastState; this.stallGraceUntil = now + 250; }
    if (!this.suspended && (elapsed > 200 || now - this.lastState > 1000 || this.predictor.tooOld(now))) this.recover();
    if (now >= this.stallGraceUntil && (now - this.lastState > 3000 || (this.predictor.waiting && now - this.barrierTime > 3000))) { this.disconnect(); this.status = 'Connection stalled — reconnect'; this.changed(); return new Map(); }
    const active = !this.suspended && !this.recovering && !this.predictor.waiting;
    const interaction = this.ownInteraction;
    const locked = interaction && interaction.phase !== 'invited' && interaction.kind !== 'gesture';
    if (locked && (direction.x !== 0 || direction.z !== 0 || direction.yaw !== undefined)) {
      // Movement is an explicit cancellation; hold the anchor until the server
      // releases it rather than predicting through a seat/other participant.
      if (!this.readiness.has('cancel')) { this.readiness.set('cancel', 0); this.interact('cancel', interaction.id); }
    } else if (!locked) this.readiness.delete('cancel');
    if (locked) direction = { x: 0, z: 0 };
    if (active) {
      this.accumulator += elapsed;
      let steps = 0;
      while (this.accumulator >= 50 && steps++ < 4) {
        const stepDirection = this.previewDirection ?? direction;
        this.previewDirection = null;
        this.accumulator -= 50; this.input(stepDirection.x, stepDirection.z, stepDirection.yaw);
        if (!this.predictor || this.predictor.waiting) break;
      }
    } else { this.accumulator = 0; this.previewDirection = null; }
    if (!this.predictor || !this.state.map) return new Map();
    const result = this.remote.sample(now, this.state.map, this.clock.estimate);
    // Once part of a moving step is visible, finish that same step before
    // changing direction or stopping. Discarding it would rewind up to 20 cm.
    if (active && this.accumulator > 0 && !this.previewDirection && (direction.x !== 0 || direction.z !== 0 || direction.yaw !== undefined)) this.previewDirection = { ...direction };
    const self = this.predictor.sample(active ? this.previewDirection ?? direction : { x: 0, z: 0 }, this.accumulator, Math.min(elapsed / 1000, .2));
    if (self) result.set(self.id, self);
    for (const i of this.interactions) if (i.phase !== 'invited' && i.kind !== 'gesture') for (const role of i.roles) {
      const entity = this.state.entities.get(role.entityId);
      if (entity) result.set(entity.id, { ...entity, position: role.position, rotation: role.rotation, moving: false });
    }
    return result;
  }
  get diagnostics(): Record<string, number | boolean> {
    const seconds = Math.max(1, (performance.now() - this.started) / 1000);
    return {
      pendingInputs: this.predictor?.pendingCount ?? 0, waitingForStop: this.predictor?.waiting ?? false,
      corrections: this.predictor?.corrections ?? 0, hardSnaps: this.predictor?.hardSnaps ?? 0,
      maxCorrection: this.predictor?.maxCorrection ?? 0, lastCorrection: this.predictor?.lastCorrection ?? 0,
      remoteSamples: this.remote.sampleCount, interpolationMS: this.remote.bufferMS, recoveries: this.recoveries,
      playbackLagMS: this.remote.playbackLagMS, stateAgeMS: Math.max(0, performance.now() - this.lastState),
      receivedBytes: this.receivedBytes, sentBytes: this.sentBytes,
      receivedBytesPerSecond: this.receivedBytes / seconds, sentBytesPerSecond: this.sentBytes / seconds,
    };
  }
  serverTime(now = performance.now()): number { const clock = this.clock.estimate; return clock ? now + clock.offset : this.latestServerMS + Math.max(0, now - this.lastState); }
  private send(message: ClientMessage): void {
    if (this.socket?.readyState !== WebSocket.OPEN) return;
    if (this.socket.bufferedAmount > 8192) { this.disconnect(); this.status = 'Connection stalled — reconnect'; this.changed(); return; }
    const text = JSON.stringify(message); this.sentBytes += new TextEncoder().encode(text).length; this.socket.send(text);
  }
  disconnect(): void {
    this.connectGeneration++;
    for (const pending of this.editRequests.values()) { clearTimeout(pending.timer); pending.reject(new Error('Disconnected')); }
    this.editRequests.clear(); this.edits.clear();
    const ws = this.socket; this.socket = null;
    if (ws) ws.close();
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null; this.pending.clear(); this.seq = 0; this.pingId = 0;
    this.predictor = null; this.frameTime = null; this.accumulator = 0; this.previewDirection = null; this.lastState = 0; this.suspended = false; this.recovering = false;
    this.receivedBytes = 0; this.sentBytes = 0; this.recoveries = 0; this.barrierTime = 0; this.stallGraceUntil = 0; this.pauseGraceState = -1; this.remote.intervalMS = 50; this.clockWarming = false;
    this.state.reset(); this.remote.reset(); this.clock.reset(); this.status = 'Disconnected';
    this.chat = []; this.socialError = '';
    this.interactions = []; this.interactionError = ''; this.interactionSeq = 0; this.readiness.clear();
    this.interactionStarting = 0; this.selectedInteraction = null;
    this.latestServerMS = 0;
  }
}
